// frontend/src/components/LoginModal.tsx
import React, { useMemo, useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useAuth } from '../contexts/FirebaseAuthContext';
import DevLogin from './DevLogin';
import { logger } from '../services/logger/BrowserLogger';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type ProviderId = 'google' | 'github' | 'apple';

interface ProviderConfig {
  id: ProviderId;
  label: string;
  icon: React.ReactNode;
  theme: 'light' | 'dark';
  onPress: (rememberMe?: boolean) => Promise<void>;
}

interface LastAccount {
  email: string;
  displayName?: string;
  provider: ProviderId;
}

const GoogleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
    <g fill="none" fillRule="evenodd">
      <path d="M17.64 9.2045c0-.6387-.0573-1.2523-.1644-1.8405H9v3.4813h4.8445c-.2092 1.1257-.8432 2.078-1.796 2.717l2.2604 2.2604c1.702-1.567 2.684-3.875 2.684-6.6182z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.8055 5.9565-2.1824l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.3283-1.5843-5.0363-3.7108H.957v2.332C2.4383 15.9825 5.482 18 9 18z" fill="#34A853" />
      <path d="M3.9638 10.7088c-.1796-.54-.2818-1.1172-.2818-1.7088 0-.5915.1022-1.1688.2818-1.7088V4.961H.957C.3473 6.1733 0 7.548 0 9s.3473 2.8268.957 4.039l3.0068-2.3302z" fill="#FBBC05" />
      <path d="M9 3.582c1.3213 0 2.5083.4537 3.4402 1.3449l2.5814-2.58C13.4632.891 11.4255 0 9 0 5.482 0 2.4383 2.0175.957 4.961l3.0068 2.3322C4.6717 5.166 6.6557 3.582 9 3.582z" fill="#EA4335" />
    </g>
  </svg>
);

const GitHubIcon = () => (
  <svg width="18" height="18" viewBox="0 0 16 16" aria-hidden="true">
    <path
      fill="currentColor"
      fillRule="evenodd"
      d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82.64-.18 1.32-.27 2-.27.68 0 1.36.09 2 .27 1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.013 8.013 0 0016 8c0-4.42-3.58-8-8-8z"
    />
  </svg>
);

const AppleIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
    <path
      fill="currentColor"
      d="M16.365 1.43c0 1.14-.477 2.214-1.246 2.982-.774.773-2.037 1.37-3.085 1.29-.138-1.102.522-2.264 1.283-2.99.808-.777 2.155-1.34 3.048-1.282zM21.6 17.414c-.57 1.32-.84 1.914-1.574 3.09-1.02 1.653-2.459 3.712-4.277 3.727-1.601.015-2.015-1.096-4.195-1.084-2.18.013-2.646 1.1-4.25 1.084-1.818-.016-3.289-1.86-4.31-3.515-2.953-4.765-3.276-10.35-1.442-13.287 1.32-2.17 3.41-3.437 5.372-3.437 1.989 0 3.24 1.14 4.873 1.14 1.595 0 2.565-1.14 4.874-1.14 1.785 0 3.674.974 4.996 2.75-4.384 2.407-3.695 8.675.933 10.672z"
    />
  </svg>
);

export const LoginModal: React.FC<LoginModalProps> = ({ isOpen, onClose, onSuccess }) => {
  const { signInWithGoogle, signInWithGithub, signInWithApple, currentUser } = useAuth();
  const [pendingProvider, setPendingProvider] = useState<ProviderId | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastAccount, setLastAccount] = useState<LastAccount | null>(null);
  const [signInProviderTracking, setSignInProviderTracking] = useState<ProviderId | null>(null);

  const isDevelopment = process.env.NODE_ENV === 'development';
  const useAuthEmulator = process.env.REACT_APP_USE_AUTH_EMULATOR === 'true';

  // All hooks must be called before any early returns
  const providers = useMemo<ProviderConfig[]>(() => [
    {
      id: 'google',
      label: 'Continue with Google',
      icon: <GoogleIcon />,
      theme: 'light',
      onPress: signInWithGoogle,
    },
    {
      id: 'github',
      label: 'Continue with GitHub',
      icon: <GitHubIcon />,
      theme: 'light',
      onPress: signInWithGithub,
    },
    {
      id: 'apple',
      label: 'Continue with Apple',
      icon: <AppleIcon />,
      theme: 'light',
      onPress: signInWithApple,
    },
  ], [signInWithApple, signInWithGithub, signInWithGoogle]);

  // Load last account from localStorage when modal opens
  useEffect(() => {
    if (isOpen) {
      const stored = localStorage.getItem('aiprep_last_account');
      if (stored) {
        try {
          const parsed = JSON.parse(stored);
          setLastAccount(parsed);
        } catch (e) {
          logger.error('Failed to parse last account', e as Error);
          localStorage.removeItem('aiprep_last_account');
        }
      }
      setError(null);
    }
  }, [isOpen]);

  // Save account to localStorage after successful sign-in
  useEffect(() => {
    if (currentUser && signInProviderTracking) {
      const accountData: LastAccount = {
        email: currentUser.email || '',
        displayName: currentUser.displayName || undefined,
        provider: signInProviderTracking
      };
      localStorage.setItem('aiprep_last_account', JSON.stringify(accountData));
      setLastAccount(accountData);
      setSignInProviderTracking(null);
    }
  }, [currentUser, signInProviderTracking]);

  const portalTarget = typeof document !== 'undefined' ? document.body : null;
  if (!isOpen) {
    return null;
  }

  if (isDevelopment && useAuthEmulator) {
    return <DevLogin onClose={onSuccess} />;
  }

  // Fallback: if portal target doesn't exist (SSR, testing), render modal directly
  if (!portalTarget) {
    logger.warn('Portal target not available, rendering modal directly');
    // Will render modal content directly below (outside portal)
  }

  const handleProvider = async (provider: ProviderConfig) => {
    try {
      setPendingProvider(provider.id);
      setError(null);
      setSignInProviderTracking(provider.id);

      // Trigger redirect - page will navigate away immediately
      // No code after this will execute (page redirects to OAuth provider)
      await provider.onPress(true);

      // Note: The lines below will NOT execute because signInWithRedirect
      // causes a full page redirect to the OAuth provider.
      // When user returns, the app will reload fresh and useAuthRedirectHandler
      // will process the result.
    } catch (err: unknown) {
      // Only errors BEFORE redirect (e.g., network issues, invalid config) will be caught
      logger.error('Sign-in redirect failed', err as Error);
      const message = err instanceof Error ? err.message : 'Failed to sign in. Please try again.';
      setError(message);
      setSignInProviderTracking(null);
      setPendingProvider(null);
    }
  };

  const handleRemoveLastAccount = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLastAccount(null);
    localStorage.removeItem('aiprep_last_account');
  };

  const getProviderIcon = (provider: ProviderId) => {
    switch (provider) {
      case 'google': return <GoogleIcon />;
      case 'github': return <GitHubIcon />;
      case 'apple': return <AppleIcon />;
      default: return null;
    }
  };

  const modalContent = (
    <div className="login-modal-overlay" onClick={onClose}>
      <div
        className="login-modal-card"
        role="dialog"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        <button
          className="login-modal-close"
          onClick={onClose}
          aria-label="Close sign in dialog"
        >
          ×
        </button>

        <header className="login-modal-header">
          <h2>{lastAccount ? 'Log back in' : 'Sign in to continue'}</h2>
          {lastAccount && <p>Choose an account to continue.</p>}
        </header>

        <div className="login-modal-body">
          {/* Quick re-auth for returning users */}
          {lastAccount && (
            <>
              <button
                type="button"
                className="login-quick-auth"
                onClick={() => {
                  const provider = providers.find(p => p.id === lastAccount.provider);
                  if (provider) {
                    handleProvider(provider);
                  } else {
                    logger.warn('Provider not found for last account', { provider: lastAccount.provider });
                    // Clear invalid lastAccount from storage
                    localStorage.removeItem('aiprep_last_account');
                  }
                }}
                disabled={pendingProvider !== null}
              >
                <span className="login-quick-auth-icon">
                  {getProviderIcon(lastAccount.provider)}
                </span>
                <span className="login-quick-auth-details">
                  <span className="login-quick-auth-name">
                    {lastAccount.displayName || lastAccount.email}
                  </span>
                  {lastAccount.displayName && (
                    <span className="login-quick-auth-email">{lastAccount.email}</span>
                  )}
                </span>
                <button
                  type="button"
                  className="login-quick-auth-remove"
                  onClick={handleRemoveLastAccount}
                  aria-label="Remove saved account"
                >
                  ×
                </button>
              </button>

              <div className="login-divider">
                <span>OR</span>
              </div>
            </>
          )}

          {/* All provider buttons */}
          {providers.map(provider => (
            <button
              key={provider.id}
              type="button"
              className={`login-provider login-provider--${provider.theme}`}
              onClick={() => handleProvider(provider)}
              disabled={pendingProvider !== null}
            >
              <span className="login-provider-icon">{provider.icon}</span>
              <span className="login-provider-label">{provider.label}</span>
              <span className="login-provider-arrow" aria-hidden="true">→</span>
            </button>
          ))}

          {/* Loading indicator during redirect */}
          {pendingProvider && (
            <div className="login-modal-loading">
              <span className="login-modal-loading-spinner"></span>
              <span>Redirecting to {providers.find(p => p.id === pendingProvider)?.label}...</span>
            </div>
          )}

          {error && <div className="login-modal-error">{error}</div>}
        </div>
      </div>
    </div>
  );

  // Use portal if available, otherwise render directly
  return portalTarget ? ReactDOM.createPortal(modalContent, portalTarget) : modalContent;
};
